// Client mirror of the server canonical/hreflang model
// (server/src/seo/marketingRoutes.ts). Keeps the metadata SeoHead re-affirms
// after hydration identical to what Fastify injected into the initial HTML, so
// a crawler that renders the SPA sees the same canonical + alternates:
//   - Arabic  → https://www.raqib360.com/ar/...
//   - English → https://www.nexusworkforce360.com/...
//   - French  → https://www.nexusworkforce360.com/fr/...
//   - x-default → Nexus English

import { getMarketingRouteLocales, marketingPageBySlug, type PublicMarketingLocale } from './marketingPages'

export const NEXUS_ORIGIN = 'https://www.nexusworkforce360.com'
export const RAQIB_ORIGIN = 'https://www.raqib360.com'
export const RAQIB_BRAND = 'رقيب 360'

const LOCAL_MARKETING_HOSTS = new Set(['localhost', '127.0.0.1', '::1'])
const PRODUCTION_MARKETING_HOSTS = new Set([
  'nexusworkforce360.com',
  'www.nexusworkforce360.com',
  'raqib360.com',
  'www.raqib360.com',
])

/**
 * Accept only the browser hosts the application intentionally serves. This
 * preserves local previews and recognized production hosts without turning an
 * arbitrary Host/X-Forwarded-Host value into trusted metadata.
 */
export const safeMarketingOrigin = (candidate?: string | null): string | null => {
  if (!candidate) return null
  try {
    const url = new URL(candidate)
    const hostname = url.hostname.toLowerCase()
    if (LOCAL_MARKETING_HOSTS.has(hostname) && (url.protocol === 'http:' || url.protocol === 'https:')) return url.origin
    if (PRODUCTION_MARKETING_HOSTS.has(hostname) && url.protocol === 'https:') return url.origin
  } catch {
    // Invalid input falls through to the configured public production origin.
  }
  return null
}

export type SeoContentKind = 'home' | 'marketing' | 'demo'
export type Alternate = { hreflang: string; href: string }

const URL_LOCALE_PREFIX = /^\/(en|ar|fr)(?:\/|$)/i

/** Explicit locale prefix on a public marketing URL (en/ar/fr), or null. */
export const readUrlLocale = (pathname: string): PublicMarketingLocale | null => {
  const match = pathname.match(URL_LOCALE_PREFIX)
  return match ? (match[1].toLowerCase() as PublicMarketingLocale) : null
}

/** Path remainder after stripping a leading locale prefix and surrounding slashes. */
export const publicRouteRest = (pathname: string): string =>
  pathname.replace(/^\/+|\/+$/g, '').replace(/^(en|ar|fr)(?:\/|$)/i, '').replace(/\/+$/, '')

export type PublicPathInfo = {
  /** True for indexable, locale-canonicalized marketing content (home/marketing/demo). */
  isPublic: boolean
  kind: SeoContentKind | null
  slug: string | null
  /** Explicit locale prefix from the URL, if any. */
  urlLocale: PublicMarketingLocale | null
}

/**
 * Classify a browser path as public marketing content. Used by both the router
 * and locale detection so that only these routes keep their /ar/ and /fr/
 * prefixes visible and treat the URL locale as authoritative.
 */
export const classifyPublicPath = (pathname: string): PublicPathInfo => {
  const urlLocale = readUrlLocale(pathname)
  const rest = publicRouteRest(pathname)
  if (rest === '' || /^(raqib360|landing)$/i.test(rest)) {
    return { isPublic: true, kind: 'home', slug: null, urlLocale }
  }
  if (rest === 'demo-request') return { isPublic: true, kind: 'demo', slug: null, urlLocale }
  if (marketingPageBySlug.has(rest)) return { isPublic: true, kind: 'marketing', slug: rest, urlLocale }
  return { isPublic: false, kind: null, slug: null, urlLocale }
}

export type PublicLocaleResolution = { isPublic: boolean; locale: PublicMarketingLocale }

/**
 * Resolve the canonical page locale for a public marketing route. Precedence:
 *   1. Explicit URL locale prefix (authoritative — wins over localStorage)
 *   2. Server-provided <html lang> (document locale)
 *   3. Saved localStorage preference
 *   4. Default English
 *
 * Because the server injects the correct <html lang> for every public route,
 * tier 2 resolves an unprefixed canonical English URL to English even when a
 * visitor previously saved an Arabic/French preference, and tier 3 (localStorage)
 * is only a dev/preview fallback. Non-public paths return isPublic:false so the
 * caller can apply application-route behavior instead.
 */
export const resolvePublicPageLocale = (input: {
  pathname: string
  documentLocale?: PublicMarketingLocale | null
  storedLocale?: PublicMarketingLocale | null
}): PublicLocaleResolution => {
  const info = classifyPublicPath(input.pathname)
  if (!info.isPublic) return { isPublic: false, locale: 'en' }
  if (info.urlLocale) return { isPublic: true, locale: info.urlLocale }
  if (input.documentLocale) return { isPublic: true, locale: input.documentLocale }
  if (input.storedLocale) return { isPublic: true, locale: input.storedLocale }
  return { isPublic: true, locale: 'en' }
}

export const OG_LOCALE: Record<PublicMarketingLocale, string> = { en: 'en_US', ar: 'ar_AR', fr: 'fr_FR' }
export const HTML_DIR: Record<PublicMarketingLocale, 'ltr' | 'rtl'> = { en: 'ltr', ar: 'rtl', fr: 'ltr' }

export const routeHasLocale = (
  kind: SeoContentKind,
  slug: string | null,
  locale: PublicMarketingLocale,
): boolean => {
  if (locale === 'en') return true
  if (kind !== 'marketing' || !slug) return true // home exists in all three locales
  return getMarketingRouteLocales(slug).includes(locale)
}

export const canonicalUrl = (
  kind: SeoContentKind,
  slug: string | null,
  locale: PublicMarketingLocale,
): string => {
  if (kind === 'demo') return `${NEXUS_ORIGIN}/demo-request`
  if (kind === 'home') {
    if (locale === 'ar') return `${RAQIB_ORIGIN}/ar/`
    if (locale === 'fr') return `${NEXUS_ORIGIN}/fr/`
    return `${NEXUS_ORIGIN}/`
  }
  const s = slug as string
  if (locale === 'ar') return `${RAQIB_ORIGIN}/ar/${s}`
  if (locale === 'fr') return `${NEXUS_ORIGIN}/fr/${s}`
  return `${NEXUS_ORIGIN}/${s}`
}

const canonicalPath = (kind: SeoContentKind, slug: string | null, locale: PublicMarketingLocale): string => {
  if (kind === 'demo') return '/demo-request'
  if (kind === 'home') return locale === 'ar' ? '/ar/' : locale === 'fr' ? '/fr/' : '/'
  return locale === 'ar' ? `/ar/${slug}` : locale === 'fr' ? `/fr/${slug}` : `/${slug}`
}

/** Canonical URL for hydrated metadata, preserving a validated request host. */
export const runtimeCanonicalUrl = (
  kind: SeoContentKind,
  slug: string | null,
  locale: PublicMarketingLocale,
  candidateOrigin?: string | null,
): string => {
  const browserOrigin = candidateOrigin === undefined && typeof window !== 'undefined' ? window.location.origin : candidateOrigin
  const origin = safeMarketingOrigin(browserOrigin)
  return origin ? `${origin}${canonicalPath(kind, slug, locale)}` : canonicalUrl(kind, slug, locale)
}

/** Same-host route path used by visible marketing links. */
export const marketingPathForLocale = (slug: string, locale: PublicMarketingLocale): string => {
  if (locale === 'ar') return `/ar/${slug}`
  if (locale === 'fr') return `/fr/${slug}`
  return `/${slug}`
}

export const hreflangAlternates = (kind: SeoContentKind, slug: string | null): Alternate[] => {
  if (kind === 'demo') {
    return [
      { hreflang: 'en', href: `${NEXUS_ORIGIN}/demo-request` },
      { hreflang: 'x-default', href: `${NEXUS_ORIGIN}/demo-request` },
    ]
  }
  const alternates: Alternate[] = []
  if (routeHasLocale(kind, slug, 'ar')) alternates.push({ hreflang: 'ar', href: canonicalUrl(kind, slug, 'ar') })
  alternates.push({ hreflang: 'en', href: canonicalUrl(kind, slug, 'en') })
  if (routeHasLocale(kind, slug, 'fr')) alternates.push({ hreflang: 'fr', href: canonicalUrl(kind, slug, 'fr') })
  alternates.push({ hreflang: 'x-default', href: canonicalUrl(kind, slug, 'en') })
  return alternates
}

export const ogLocaleAlternates = (
  kind: SeoContentKind,
  slug: string | null,
  locale: PublicMarketingLocale,
): string[] =>
  hreflangAlternates(kind, slug)
    .filter((alt) => alt.hreflang !== 'x-default' && alt.hreflang !== locale)
    .map((alt) => OG_LOCALE[alt.hreflang as PublicMarketingLocale])
    .filter(Boolean)

/** Re-brand the English product suffix to Raqib360 in Arabic titles. */
export const brandTitleForLocale = (title: string, locale: PublicMarketingLocale): string =>
  locale === 'ar' ? title.replace(/NexusWorkforce360/g, RAQIB_BRAND) : title

/** Destination a marketing locale switcher should navigate to (canonical URL). */
export const localeSwitchDestination = (
  kind: SeoContentKind,
  slug: string | null,
  locale: PublicMarketingLocale,
): string =>
  routeHasLocale(kind, slug, locale) ? canonicalUrl(kind, slug, locale) : canonicalUrl(kind, slug, 'en')

/**
 * Locale-correct JSON-LD graph matching the server injection (Organization,
 * WebSite, SoftwareApplication, optional BreadcrumbList + FAQPage). Arabic uses
 * the Raqib brand/origin and inLanguage=ar; the canonical URL is the page's
 * canonical. Keeps the hydrated structured data consistent with initial HTML.
 */
export const buildLocalizedStructuredData = (params: {
  kind: SeoContentKind
  slug: string | null
  locale: PublicMarketingLocale
  description: string
  eyebrow?: string
  faq?: Array<{ question: string; answer: string }>
}): unknown[] => {
  const { kind, slug, locale, description, eyebrow, faq } = params
  const canonical = canonicalUrl(kind, slug, locale)
  const brand = locale === 'ar' ? RAQIB_BRAND : 'NexusWorkforce360'
  const home = canonicalUrl('home', null, locale)
  const homeLabel = locale === 'ar' ? 'الرئيسية' : locale === 'fr' ? 'Accueil' : 'Home'
  const nodes: unknown[] = [
    {
      '@context': 'https://schema.org', '@type': 'Organization', name: brand,
      alternateName: locale === 'ar' ? ['NexusWorkforce360'] : [RAQIB_BRAND],
      url: home, logo: `${NEXUS_ORIGIN}/landing/nexusworkforce360-logo.svg`, inLanguage: locale,
    },
    { '@context': 'https://schema.org', '@type': 'WebSite', url: home, name: brand, inLanguage: locale },
    {
      '@context': 'https://schema.org', '@type': 'WebPage', name: eyebrow || brand,
      description, url: canonical, inLanguage: locale, isPartOf: { '@type': 'WebSite', name: brand, url: home },
    },
    {
      '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: brand,
      applicationCategory: 'BusinessApplication', operatingSystem: 'Web, Android',
      description, url: canonical, inLanguage: locale, brand: { '@type': 'Brand', name: brand },
      offers: {
        '@type': 'Offer', price: '0', priceCurrency: 'USD',
        description: locale === 'ar'
          ? 'عمليات القوى العاملة الأساسية مجانية. تتوفر خيارات مدفوعة منفصلة للتسريع والتكاملات والتقارير المتقدمة والذكاء الاصطناعي والدعم والمؤسسات.'
          : locale === 'fr'
            ? 'Les opérations workforce essentielles sont gratuites. Accélération, intégrations, reporting avancé, IA, support et Enterprise sont configurés séparément.'
            : 'Free core workforce operations. Optional paid acceleration, integrations, advanced reporting, AI, support, and enterprise controls are configured separately.',
      },
    },
  ]
  if (kind === 'marketing' && eyebrow) {
    nodes.push({
      '@context': 'https://schema.org', '@type': 'BreadcrumbList', inLanguage: locale,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: homeLabel, item: home },
        { '@type': 'ListItem', position: 2, name: eyebrow, item: canonical },
      ],
    })
  }
  if (faq && faq.length > 0) {
    nodes.push({
      '@context': 'https://schema.org', '@type': 'FAQPage', inLanguage: locale,
      mainEntity: faq.map((item) => ({
        '@type': 'Question', name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    })
  }
  return nodes
}
